// external
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter, Action } from 'vuex-class';

// models
import { IPromotion } from '@/interfaces/Promotion';
import MxrRetailer from '@/interfaces/MxrRetailer';
import { PromotionCarouselItem } from '@/interfaces/PromotionCarouselItem';
import { VuexAction } from '@/@types';

// store
import {
	GET_CURRENT_MXR_RETAILER,
	UPDATE_VISIBLE_PROMOTION_IDS,
	GET_PROMOTIONS,
	GET_BUMP_ANIMATION,
	GET_RANDOMIZE_OFFERS_DISPLAYED,
	GET_IS_DEFAULT,
	SET_PREVIOUS_PROMOTION,
	GET_CAROUSEL_CLICK_REPORT,
	SEND_REPORT,
} from '@/store/types';

// util
import { getRandomInt } from '@/utils/';
import { IReport } from '@/interfaces/IReport';

@Component({})
export default class PromotionCarousel extends Vue {
	// carouselSlot is a zero based index to describe where it is on the UI.
	// It's also used in state management for keeping track of visible promotions
	@Prop({ required: true, type: Number }) carouselSlot!: number;

	@Prop({ required: true, type: Number }) carouselId!: number;

	@Prop({ required: true, type: String }) carouselHeading!: string;

	@Getter(GET_PROMOTIONS) getPromotions!: PromotionCarouselItem[];

	@Getter(GET_BUMP_ANIMATION) getBumpAnimation!: boolean;

	@Getter(GET_RANDOMIZE_OFFERS_DISPLAYED)
	getRandomizeOffersDisplayed!: boolean;

	@Getter(GET_CURRENT_MXR_RETAILER) getCurrentMxrRetailer!: MxrRetailer;

	@Getter(GET_IS_DEFAULT) getIsDefault!: boolean;

	@Getter(GET_CAROUSEL_CLICK_REPORT) getCarouselClickReport!: IReport;

	@Action(UPDATE_VISIBLE_PROMOTION_IDS)
	updateVisiblePromotionIds!: VuexAction<any>;

	@Action(SET_PREVIOUS_PROMOTION)
	setPrevPromotion!: VuexAction<IPromotion | null>;

	@Action(SEND_REPORT) sendReport!: VuexAction<IReport>;

	// Using this to hold onto offers that show an inital impression
	// so we don't show an impression twice.
	private initialImpressionIds = new Set<string>();

	private currentPromotionIndex = -1;

	bumpAnimationClass: string = '';

	get carouselItem(): PromotionCarouselItem {
		return this.getPromotions
			.filter((c) => c.id === this.carouselId)
			.reduce((pv, cv) => cv, new PromotionCarouselItem(-1, '', [], []));
	}

	get promotions(): IPromotion[] {
		if (this.getIsDefault) {
			return this.carouselItem.defaultPromotions;
		}
		return this.carouselItem.promotions;
	}

	get price(): string {
		if (!this.currentPromotion) return 'See store for price';
		return this.currentPromotion && this.currentPromotion.price === '$0.00'
			? 'See cart for price'
			: this.currentPromotion.price;
	}

	@Watch('promotions')
	promotionsUpdated() {
		if (this.currentPromotionIndex < 0) {
			this.nextPromotion(false, false);
		}
	}

	nextPromotion(
		sendOfferClickTelemetry = true,
		initialImpression = false
	): void {
		const outgoingPromotion: IPromotion | null = this.currentPromotion;
		this.setPrevPromotion(outgoingPromotion);

		if (this.promotions.length === 0) {
			return;
		}
		if (
			this.currentPromotionIndex < 0 ||
			this.currentPromotionIndex + 1 >= this.promotions.length
		) {
			this.currentPromotionIndex = 0;
		} else {
			this.currentPromotionIndex += 1;
		}

		// Send Telemetry
		if (
			sendOfferClickTelemetry &&
			outgoingPromotion &&
			this.currentPromotion
		) {
			this.sendReport(this.getCarouselClickReport);
		}

		if (this.currentPromotion) {
			this.sendImpressionTelemetry(initialImpression);
			this.updateVisiblePromotionIds({
				carouselSlot: this.carouselSlot,
				incomingId: this.currentPromotion.id,
				uniqueId: this.currentPromotion.uniqueId,
			});
		}
	}

	get currentPromotion(): IPromotion | null {
		if (this.currentPromotionIndex < 0) {
			this.currentPromotionIndex = this.promotions.length ? 0 : -1;
		}
		return this.currentPromotionIndex >= 0
			? this.promotions[this.currentPromotionIndex]
			: null;
	}

	private bumpTheItem(): void {
		if (this.promotions.length <= 1 || this.getBumpAnimation === false)
			return;
		setTimeout(() => {
			this.bumpAnimationClass = 'bump';
			setTimeout(() => {
				this.bumpAnimationClass = '';
			}, 600);
		}, 500 * this.carouselSlot);
	}

	private sendImpressionTelemetry(initialImpression = false): void {
		if (!this.currentPromotion) return;
		if (
			initialImpression &&
			this.initialImpressionIds.has(this.currentPromotion.id.toString())
		)
			return;
		if (initialImpression) {
			this.initialImpressionIds.add(this.currentPromotion.id.toString());
		}
	}

	public created(): void {
		const self = this;

		// Randomize Offers
		if (self.getRandomizeOffersDisplayed && self.promotions.length) {
			self.currentPromotionIndex = getRandomInt(self.promotions.length);
			if (this.currentPromotion) {
				this.sendImpressionTelemetry(true);
				this.updateVisiblePromotionIds({
					carouselSlot: this.carouselSlot,
					incomingId: this.currentPromotion.id,
					uniqueId: this.currentPromotion.uniqueId,
				});
			} else {
				this.nextPromotion(false, true);
			}
			setTimeout(() => self.bumpTheItem(), 500);
		} else {
			this.nextPromotion(false, true);
		}
	}
}
